import { useRef, useState, useEffect, useCallback } from 'react';

interface HorizontalScrollProps {
  children: React.ReactNode;
  gap?: number;
}

export default function HorizontalScroll({ children, gap = 20 }: HorizontalScrollProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [showLeft, setShowLeft] = useState(false);
  const [showRight, setShowRight] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const dragRef = useRef({ down: false, startX: 0, scrollStart: 0, moved: false });

  const updateArrows = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setShowLeft(el.scrollLeft > 4);
    setShowRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 4);
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    updateArrows();
    el.addEventListener('scroll', updateArrows, { passive: true });
    const ro = new ResizeObserver(updateArrows);
    ro.observe(el);
    return () => {
      el.removeEventListener('scroll', updateArrows);
      ro.disconnect();
    };
  }, [updateArrows]);

  const scroll = (dir: 'left' | 'right') => {
    const el = scrollRef.current;
    if (!el) return;
    const amount = el.clientWidth * 0.7;
    el.scrollBy({ left: dir === 'left' ? -amount : amount, behavior: 'smooth' });
  };

  const onMouseDown = (e: React.MouseEvent) => {
    const el = scrollRef.current;
    if (!el) return;
    dragRef.current = { down: true, startX: e.clientX, scrollStart: el.scrollLeft, moved: false };
    setIsDragging(true);
  };

  const onMouseMove = (e: React.MouseEvent) => {
    if (!dragRef.current.down) return;
    const el = scrollRef.current;
    if (!el) return;
    const delta = Math.abs(e.clientX - dragRef.current.startX);
    if (delta > 5) {
      dragRef.current.moved = true;
      el.scrollLeft = dragRef.current.scrollStart + (dragRef.current.startX - e.clientX);
    }
  };

  const onMouseUp = () => {
    dragRef.current.down = false;
    setIsDragging(false);
  };

  const onClickCapture = (e: React.MouseEvent) => {
    if (dragRef.current.moved) {
      e.stopPropagation();
      e.preventDefault();
      dragRef.current.moved = false;
    }
  };

  return (
    <div className="relative group/scroll">
      {showLeft && (
        <button
          onClick={() => scroll('left')}
          className="absolute -left-2 top-1/2 -translate-y-1/2 z-10 w-9 h-9 md:w-10 md:h-10 flex items-center justify-center bg-background-50 border border-background-300/50 text-foreground-100 rounded-full transition-all cursor-pointer opacity-0 group-hover/scroll:opacity-100 hover:bg-background-100 hover:border-background-300 shadow-sm whitespace-nowrap"
          aria-label="Desplazar a la izquierda"
        >
          <i className="ri-arrow-left-s-line text-lg"></i>
        </button>
      )}

      <div
        ref={scrollRef}
        className="flex overflow-x-auto scrollbar-hide select-none"
        style={{ gap: `${gap}px`, cursor: isDragging ? 'grabbing' : 'grab', scrollbarWidth: 'none', msOverflowStyle: 'none' }}
        onMouseDown={onMouseDown}
        onMouseMove={onMouseMove}
        onMouseUp={onMouseUp}
        onMouseLeave={onMouseUp}
        onClickCapture={onClickCapture}
      >
        {children}
      </div>

      {showRight && (
        <button
          onClick={() => scroll('right')}
          className="absolute -right-2 top-1/2 -translate-y-1/2 z-10 w-9 h-9 md:w-10 md:h-10 flex items-center justify-center bg-background-50 border border-background-300/50 text-foreground-100 rounded-full transition-all cursor-pointer opacity-0 group-hover/scroll:opacity-100 hover:bg-background-100 hover:border-background-300 shadow-sm whitespace-nowrap"
          aria-label="Desplazar a la derecha"
        >
          <i className="ri-arrow-right-s-line text-lg"></i>
        </button>
      )}
    </div>
  );
}